import React, { useState, useEffect } from 'react';
import { X, ShoppingCart, Check, AlertTriangle, Zap, Package, Info } from 'lucide-react';
import ProductIcon, { BigProductWatermark } from './ProductIcon';
import { useApp } from '../context/AppContext';
import { toast } from '../hooks/use-toast';

const ProductDetailModal = ({ product, open, onClose, onDeposit }) => {
  const { wallet, buy } = useApp();
  const [currency, setCurrency] = useState('usdt');
  const [step, setStep] = useState('detail'); // detail | confirm | success
  const [processing, setProcessing] = useState(false);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (open) {
      setStep('detail');
      setCurrency('usdt');
      setOrder(null);
    }
  }, [open, product?.id]);

  if (!open || !product) return null;

  const price = Number(currency === 'usdt' ? product.usdt : product.inr);
  const balance = Number(wallet?.[currency] || 0);
  const canAfford = balance >= price;
  const outOfStock = (product.stock || 0) <= 0;
  const symbol = currency === 'usdt' ? '$' : '₹';

  const doBuy = async () => {
    if (processing) return;
    setProcessing(true);
    const res = await buy(product, currency);
    setProcessing(false);
    if (!res.ok) {
      toast({ title: 'Purchase failed', description: res.msg });
      return;
    }
    setOrder(res.order);
    setStep('success');
  };

  const closeAll = () => {
    setStep('detail');
    setOrder(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeAll} />

      <div className="relative w-full max-w-[560px] border border-[#1f1f1f] bg-[#0a0a0a] rounded-md font-mono my-auto overflow-hidden">
        {/* Subtle watermark */}
        <div className="absolute inset-0 pointer-events-none opacity-70">
          <BigProductWatermark type={product.icon} />
        </div>

        <div className="relative">
          {/* Header */}
          <div className="px-6 py-4 border-b border-[#151515] flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#22c55e]">
              <ShoppingCart size={14} />
              <span className="text-[10px] tracking-[0.25em]">
                {step === 'success' ? 'ORDER COMPLETE' : 'PRODUCT DETAILS'}
              </span>
            </div>
            <button onClick={closeAll} className="text-[#5a5a5a] hover:text-[#e6e6e6]">
              <X size={16} />
            </button>
          </div>

          {step === 'success' ? (
            <div className="px-6 py-8 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#22c55e]/10 border-2 border-[#22c55e] flex items-center justify-center mb-4">
                <Check size={26} className="text-[#22c55e]" />
              </div>
              <h2 className="text-xl text-[#e6e6e6] mb-1">
                {order?.status === 'DELIVERED' ? 'Order Delivered!' : 'Order Placed!'}
              </h2>
              <p className="text-[12px] text-[#8a8a8a] mb-4">
                <code className="text-[#22c55e]">{order?.id}</code> · {product.name}
              </p>
              {order?.status === 'DELIVERED' && order?.credential ? (
                <div className="text-left bg-[#050505] border border-[#151515] rounded-md p-3 mb-4">
                  <div className="text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1.5">YOUR CREDENTIAL / KEY</div>
                  <code className="text-[12px] text-[#22c55e] break-all whitespace-pre-wrap">{order.credential}</code>
                </div>
              ) : (
                <div className="text-[12px] text-[#f59e0b] bg-[#1a1408] border border-[#3a2a10] rounded-md p-3 mb-4">
                  ⏳ Your order is being processed. You&apos;ll receive the credentials shortly in <b>My Orders</b>.
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={closeAll}
                  className="flex-1 py-2.5 rounded-md text-[11px] tracking-[0.25em] border border-[#1f1f1f] text-[#8a8a8a] hover:border-[#22c55e] hover:text-[#22c55e] transition-colors"
                >
                  CLOSE
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Body */}
              <div className="px-6 py-5">
                <div className="flex items-start gap-3 mb-4">
                  <ProductIcon type={product.icon} size={22} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] text-[#e6e6e6] uppercase leading-snug break-words">{product.name}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] tracking-[0.2em]">
                      <span className="text-[#7a7a7a]">{product.category || 'General'}</span>
                      <span className="text-[#3a3a3a]">·</span>
                      <span className="text-[#7a7a7a] uppercase">{product.delivery_mode === 'auto' ? 'INSTANT DELIVERY' : 'MANUAL DELIVERY'}</span>
                      <span className={`ml-auto px-2 py-0.5 border rounded-sm ${
                        outOfStock ? 'text-[#ef4444] bg-[#1a0d0d] border-[#3a1a1a]'
                        : (product.stock || 0) <= 3 ? 'text-[#f59e0b] bg-[#1a1408] border-[#3a2a10]'
                        : 'text-[#22c55e] bg-[#0f1a12] border-[#1f3a24]'
                      }`}>
                        {outOfStock ? 'OUT OF STOCK' : `${product.stock} LEFT`}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {product.description ? (
                  <div className="mb-4 border border-[#151515] rounded-md p-3 bg-[#080808]">
                    <div className="text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1.5 flex items-center gap-1">
                      <Info size={10} /> DESCRIPTION
                    </div>
                    <p className="text-[12px] text-[#c8c8c8] leading-relaxed whitespace-pre-wrap">{product.description}</p>
                  </div>
                ) : (
                  <div className="mb-4 border border-dashed border-[#151515] rounded-md p-3 text-[11px] text-[#5a5a5a]">
                    <Info size={10} className="inline mr-1" />
                    Digital product · delivered as account credentials, key or activation link.
                  </div>
                )}

                {/* Feature strip */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="border border-[#151515] rounded-md px-2 py-2 bg-[#080808] text-center">
                    <Zap size={12} className="mx-auto text-[#22c55e] mb-1" />
                    <div className="text-[9px] tracking-[0.2em] text-[#7a7a7a]">INSTANT</div>
                  </div>
                  <div className="border border-[#151515] rounded-md px-2 py-2 bg-[#080808] text-center">
                    <Package size={12} className="mx-auto text-[#22c55e] mb-1" />
                    <div className="text-[9px] tracking-[0.2em] text-[#7a7a7a]">VERIFIED</div>
                  </div>
                  <div className="border border-[#151515] rounded-md px-2 py-2 bg-[#080808] text-center">
                    <Check size={12} className="mx-auto text-[#22c55e] mb-1" />
                    <div className="text-[9px] tracking-[0.2em] text-[#7a7a7a]">WARRANTY</div>
                  </div>
                </div>

                {/* Currency selector */}
                <div className="mb-3">
                  <div className="text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1.5">PAY WITH</div>
                  <div className="grid grid-cols-2 gap-0 border border-[#1f1f1f] rounded-md overflow-hidden">
                    <button
                      onClick={() => setCurrency('usdt')}
                      className={`py-2 text-[11px] tracking-[0.25em] transition-colors ${currency === 'usdt' ? 'bg-[#22c55e] text-black font-semibold' : 'text-[#8a8a8a] hover:text-[#e6e6e6]'}`}
                    >USDT · ${Number(product.usdt).toFixed(2)}</button>
                    <button
                      onClick={() => setCurrency('inr')}
                      className={`py-2 text-[11px] tracking-[0.25em] transition-colors ${currency === 'inr' ? 'bg-[#22c55e] text-black font-semibold' : 'text-[#8a8a8a] hover:text-[#e6e6e6]'}`}
                    >INR · ₹{Number(product.inr).toFixed(2)}</button>
                  </div>
                </div>

                {/* Price summary */}
                <div className="border border-[#151515] rounded-md bg-[#080808] p-3 mb-4 space-y-1.5">
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-[#7a7a7a] tracking-[0.2em]">TOTAL</span>
                    <span className="text-[#e6e6e6] text-lg">{symbol}{price.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-[#5a5a5a] tracking-[0.2em]">YOUR BALANCE</span>
                    <span className={canAfford ? 'text-[#22c55e]' : 'text-[#ef4444]'}>{symbol}{balance.toFixed(2)}</span>
                  </div>
                  {!canAfford && !outOfStock && (
                    <div className="mt-2 text-[11px] text-[#f59e0b] flex items-center gap-1.5">
                      <AlertTriangle size={11} />
                      Short by {symbol}{(price - balance).toFixed(2)} — deposit needed
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 pb-6 flex flex-col sm:flex-row gap-2">
                <button
                  onClick={closeAll}
                  className="sm:w-32 py-2.5 rounded-md text-[11px] tracking-[0.25em] border border-[#1f1f1f] text-[#8a8a8a] hover:border-[#ef4444] hover:text-[#ef4444] transition-colors"
                >
                  CANCEL
                </button>
                {outOfStock ? (
                  <button
                    disabled
                    className="flex-1 py-2.5 rounded-md text-[11px] tracking-[0.25em] border border-[#1f1f1f] text-[#5a5a5a] cursor-not-allowed"
                  >
                    OUT OF STOCK
                  </button>
                ) : !canAfford ? (
                  <button
                    onClick={() => { closeAll(); onDeposit && onDeposit(); }}
                    className="flex-1 py-2.5 rounded-md text-[11px] tracking-[0.25em] border border-[#22c55e] text-[#22c55e] hover:bg-[#0f1a12] transition-colors"
                  >
                    ADD FUNDS · DEPOSIT {currency.toUpperCase()}
                  </button>
                ) : (
                  <button
                    onClick={doBuy}
                    disabled={processing}
                    className="flex-1 py-2.5 rounded-md text-[11px] tracking-[0.25em] bg-[#166534] hover:bg-[#15803d] disabled:opacity-60 text-[#e7ffef] transition-colors flex items-center justify-center gap-2"
                  >
                    {processing ? (
                      <>PROCESSING…</>
                    ) : (
                      <><Check size={12} /> CONFIRM PURCHASE · {symbol}{price.toFixed(2)}</>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;
